import { ElementType } from 'react';

type FormatCurrencyAmountProps = {
    value?: number | null;
    withParentheses?: boolean;
    convertToHighestDenomination?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
};

/**
 * Formats a cents value as a dollar amount without a currency symbol.
 *
 * @param FormatAmountProps
 * @returns Formatted amount in string
 */
export function formatCurrencyAmount({
    value = 0,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    withParentheses = false,
    convertToHighestDenomination = true,
}: FormatCurrencyAmountProps): string {
    // Show null or undefined values as a dash
    let formatted = '-';

    if (value) {
        // Convert cents to dollars, seperate thousands by commas, and ensure there are always 2 decimal places
        formatted = new Intl.NumberFormat(undefined, {
            minimumFractionDigits,
            maximumFractionDigits,
        }).format(convertToHighestDenomination ? value / 100 : value);

        if (withParentheses) {
            formatted = `(${formatted})`;
        }
    } else if (value === 0) {
        formatted = '0.00';
    }

    return formatted;
}

type CurrencyProps = {
    value?: number | null;
    withParentheses?: boolean;
    description?: string;
    as?: ElementType;
    className?: string;
    convertToHighestDenomination?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
};

/**
 * Formats a cents value as a dollar amount and display it as a component with style.
 *
 * @param CurrencyProps
 * @returns JSX.Element
 */
function Currency({
    value,
    description,
    minimumFractionDigits,
    maximumFractionDigits,
    withParentheses,
    convertToHighestDenomination,
    as: Component,
    className,
}: CurrencyProps): JSX.Element {
    const formatted = formatCurrencyAmount({
        value,
        maximumFractionDigits,
        minimumFractionDigits,
        withParentheses,
        convertToHighestDenomination,
    });

    if (Component) {
        return (
            <Component className={className}>
                {formatted} {description}
            </Component>
        );
    }

    return (
        <p className={className}>
            {formatted} {description}
        </p>
    );
}
export default Currency;
